import { useTranslation } from "react-i18next";
import cx from "classnames";
import { UsageExplorerLocationAggregate } from "../../../types/analytics/UsageExplorerLocationAggregate";

const AGING_THRESHOLD_DAYS = 14;

interface UsageExplorerLocationCardListProps {
  rows: UsageExplorerLocationAggregate[];
  onOpenLocation: (locationId: number) => void;
}

function UsageExplorerLocationCardList({
  rows,
  onOpenLocation,
}: UsageExplorerLocationCardListProps) {
  const { t } = useTranslation();

  return (
    <ul className="divide-y divide-gray-200">
      {rows.map((row) => {
        const oldestDays = row.oldestOutstandingAgeDays;
        const ageTone =
          oldestDays !== null && oldestDays >= AGING_THRESHOLD_DAYS
            ? "text-red-600"
            : "text-gray-600";
        const canDrill = row.hasChildren && row.locationId !== null;

        return (
          <li key={row.locationId ?? "unassigned"}>
            {canDrill ? (
              <button
                type="button"
                className="flex w-full flex-col gap-2 px-4 py-4 text-left hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[color:var(--color-primary)]"
                onClick={() => onOpenLocation(row.locationId!)}
              >
                <LocationCardContent row={row} ageTone={ageTone} />
              </button>
            ) : (
              <div className="flex flex-col gap-2 px-4 py-4">
                <LocationCardContent row={row} ageTone={ageTone} />
              </div>
            )}
          </li>
        );
      })}
      {rows.length === 0 && (
        <li className="px-4 py-10 text-center text-sm text-gray-600">
          {t(
            "Analytics.Widgets.Usage.Explorer.Empty",
            "No rows match this explorer view.",
          )}
        </li>
      )}
    </ul>
  );
}

function LocationCardContent({
  row,
  ageTone,
}: {
  row: UsageExplorerLocationAggregate;
  ageTone: string;
}) {
  const { t } = useTranslation();
  return (
    <>
      <div className="flex items-start justify-between gap-3">
        <span className="font-semibold text-gray-800">
          {row.locationName ||
            t("Analytics.Widgets.Usage.Explorer.NoLocation", "No location")}
          {row.isCurrentLocation && (
            <span className="ml-1 font-normal text-gray-500">
              {t(
                "Analytics.Widgets.Usage.Explorer.ThisLocation",
                "(this location)",
              )}
            </span>
          )}
        </span>
        {row.oldestOutstandingAgeDays !== null && (
          <span className={cx("shrink-0 text-sm font-medium", ageTone)}>
            {row.oldestOutstandingAgeDays === 1
              ? t("Analytics.Widgets.Usage.Explorer.OneDay", "1 day")
              : t("Analytics.Widgets.Usage.Explorer.Days", "{{count}} days", {
                  count: row.oldestOutstandingAgeDays,
                })}
          </span>
        )}
      </div>
      <p className="text-sm text-gray-600">
        {t(
          "Analytics.Widgets.Usage.Explorer.MobileLocationSummary",
          "{{assigned}} assigned · {{outstanding}} outstanding",
          {
            assigned: row.assignedCount,
            outstanding: row.outstandingCount,
          },
        )}
      </p>
    </>
  );
}

export default UsageExplorerLocationCardList;
